import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useLazyQuery } from '@apollo/client/react';
import { toast } from 'sonner';
import { ExternalLink, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  Separator,
  cn,
} from '@scio3/ui';
import {
  UPDATE_SUBSCRIPTION_ADDON,
  EDIT_SUBSCRIPTION_ADDON,
  PLAN_FEATURES_QUERY,
  GET_UPCOMING_SUBSCRIPTION,
  GET_DEVICES_ADDON,
  GET_PRICE_AUTO_CONVERT_CURRENCIES,
} from '@scio3/graphql-client';
import type { SubscriptionAddOnStatus } from '@scio3/shared-types';
import { useConfirm } from '@/components/shared';
import { reportPaymentError } from '@/lib/telemetry/payment-telemetry';
import type { CurrencyOptionEntry } from '../utils/readAddonAmounts';
import { readAddonAmounts } from '../utils/readAddonAmounts';
import { useBillingDialogStore } from '../stores/billing-dialog-store';
import { useBillingData } from '../hooks/useBillingData';
import { ScreenCountSelector } from '../components/ScreenCountSelector';
import { ProcessingOverlay } from '../components/ProcessingOverlay';
import { AddonTierRadioCards } from '../components/AddonTierRadioCards';
import { TierDowngradeWarning } from '../components/TierDowngradeWarning';
import {
  formatCurrencyAmount,
  extractPlanFeaturesNode,
  extractMutationResponse,
  getPlanDisplayName,
} from '../utils/plan-helpers';
import { BILLING_INTERVAL_LABELS, ADDON_TIER_CATALOG } from '../utils/billing-constants';
import { OptiDevAddonBody } from './OptiDevAddonBody';
import { OPTIDEV_ADDON_KEY, isOptidevOnTrial, resolveOptidevMode } from '../utils/optidev-addon';

type Tier = 'standard' | 'pro';

type ReceiptAddon = {
  key: string;
  name: string;
  pricePerUnit: number;
  quantity: number;
  planFeature?: string;
};

/**
 * Snapshot all currently-active addons for the receipt — matches scio1's
 * `getAddOnsChecked()` which lists every active addon, not just the one being updated.
 *
 * For the addon being modified, use the NEW `updatedQuantity`; for others, keep their
 * existing `maxQuantity`. Trial / inactive / reduced-to-0 are skipped. If the addon
 * being updated isn't yet in `paymentAddons` (brand-new addon being attached), it's
 * appended manually.
 *
 * Pure function so it's testable in isolation and doesn't rely on hook state.
 */
function buildReceiptAddons(args: {
  paymentAddons: SubscriptionAddOnStatus[] | undefined;
  planFeaturesData: unknown;
  updatedAddonKey: string | undefined;
  updatedQuantity: number;
  updatedPlanFeature?: string;
  currency: string;
  intervalKey: 'monthly' | 'yearly';
  fallbackAddonName: string | undefined;
  fallbackPricePerUnit: number;
  /** Stripe currency_options for isAutoConvertCurrency addons — same shape
   *  as getPriceAutoConvertCurrencies returns. Used so the receipt total
   *  reflects what Stripe will actually charge (scio1 parity). */
  currencyOptions?: Record<string, Record<string, Record<string, CurrencyOptionEntry>>>;
}): ReceiptAddon[] {
  const planFeaturesNode = extractPlanFeaturesNode(args.planFeaturesData);
  const planAddOnsMap = planFeaturesNode?.add_ons as
    | Record<string, Record<string, unknown>>
    | undefined;

  const receiptAddons: ReceiptAddon[] = [];

  for (const sub of args.paymentAddons ?? []) {
    if (!sub.isActive || sub.isTrial) continue;
    const isUpdated = sub.key === args.updatedAddonKey;
    const effectiveQuantity = isUpdated ? args.updatedQuantity : (sub.maxQuantity ?? 0);
    if (effectiveQuantity <= 0) continue;
    const addonMeta = planAddOnsMap?.[sub.key];
    const name = (addonMeta?.name as string) ?? sub.key;
    const priceMap = addonMeta?.price as
      | Record<string, Record<string, { price?: number }>>
      | undefined;
    // Tiered addons use `{tier}_{interval}` price key — per scio1 parity.
    const tierCatalog = ADDON_TIER_CATALOG[sub.key];
    const effectiveTier = isUpdated
      ? (args.updatedPlanFeature ?? sub.planFeature)
      : sub.planFeature;
    let priceKey: string = args.intervalKey;
    if (tierCatalog && effectiveTier) {
      const tierEntry = tierCatalog.find((t) => t.tier === effectiveTier);
      if (tierEntry) {
        priceKey =
          args.intervalKey === 'monthly' ? tierEntry.priceKeyMonthly : tierEntry.priceKeyYearly;
      }
    }
    // SCIO 1.0 parity (subscriptionPlanModal.js:813-828 getTierPrice): for
    // isAutoConvertCurrency addons, prefer the Stripe-converted price from
    // args.currencyOptions; fall back to the catalog 'usd' entry; finally to
    // user's currency lookup for non-auto-convert addons.
    const isAutoConvert = !!(addonMeta as { isAutoConvertCurrency?: boolean })
      ?.isAutoConvertCurrency;
    const lookupCurrency = isAutoConvert ? 'usd' : args.currency;
    let pricePerUnit = priceMap?.[lookupCurrency]?.[priceKey]?.price ?? 0;
    if (isAutoConvert && args.currency !== 'usd') {
      const cents = args.currencyOptions?.[sub.key]?.[priceKey]?.[args.currency]?.unit_amount;
      if (typeof cents === 'number') pricePerUnit = cents / 100;
    }
    receiptAddons.push({
      key: sub.key,
      name,
      pricePerUnit,
      quantity: effectiveQuantity,
      planFeature: effectiveTier,
    });
  }

  // Brand-new addon being attached for the first time — not yet in paymentAddons
  if (
    args.updatedQuantity > 0 &&
    args.updatedAddonKey &&
    !receiptAddons.some((a) => a.key === args.updatedAddonKey)
  ) {
    receiptAddons.push({
      key: args.updatedAddonKey,
      name: args.fallbackAddonName ?? args.updatedAddonKey,
      pricePerUnit: args.fallbackPricePerUnit,
      quantity: args.updatedQuantity,
      planFeature: args.updatedPlanFeature,
    });
  }

  return receiptAddons;
}

interface FinishResult {
  invoiceNumber?: string;
  invoicePdf?: string;
  /** From getUpcomingSubscription — both already in dollars (backend divides by 100) */
  totalPrice?: number;
  credit?: number;
  upcoming?: Record<string, unknown>;
  /** Top-level tax line items from getUpcomingSubscription (matches scio1 `res.taxs`).
   *  Each entry: `{ name: 'VAT - Vietnam(10%)', tax: <cents> }`. */
  taxs?: Array<{ name?: string; tax?: number }>;
  /** Plan + screens snapshot at charge time — replaces Stripe lines.data rendering
   *  to match scio1's clean receipt format. */
  planName?: string;
  planBillingCycleLabel?: string;
  planPricePerScreen?: number;
  planScreens?: number;
  /** All currently-active addons at charge time — matches scio1's `getAddOnsChecked()`.
   *  For the addon being updated, `quantity` reflects the NEW value;
   *  for others, the existing `maxQuantity`. Trial / inactive / reduced-to-0 are skipped. */
  addons?: Array<{
    key: string;
    name: string;
    pricePerUnit: number;
    quantity: number;
    planFeature?: string;
  }>;
}

/**
 * Addon update dialog — matching scio1's subscriptionPlanModal in 'addons' mode.
 * Shows a simple number selector + price info for a single add-on.
 * Calls updateSubscriptionAddOn mutation directly (no full wizard flow).
 */
export function AddonUpdateDialog() {
  const { t } = useTranslation();
  const activeDialog = useBillingDialogStore((s) => s.activeDialog);
  const dialogContext = useBillingDialogStore((s) => s.dialogContext);
  const closeDialog = useBillingDialogStore((s) => s.closeDialog);
  const isOpen = activeDialog === 'addonUpdate';
  const addonKey = dialogContext.addonKey as string | undefined;
  const addonName = dialogContext.addonName as string | undefined;
  const isUpdate = dialogContext.isUpdate as boolean | undefined;
  const subAccountId = dialogContext.subAccountId as string | undefined;

  // useBillingData with subAccountId → fetches fresh sub-account data from server
  const {
    payment,
    addons: paymentAddons,
    defaultEmail: email,
    currency,
    currencySymbol,
    existingCard,
    loadingSubAccount,
    maxScreens,
  } = useBillingData({ subAccountId, skip: !isOpen });

  const extPayment = payment as (typeof payment & { plan?: string }) | undefined;
  const currentPlanAlias = (extPayment?.planFeature as string) ?? 'standard';

  // Get current addon subscription data
  const currentAddonSub = useMemo(() => {
    return paymentAddons?.find((s) => s.key === addonKey);
  }, [paymentAddons, addonKey]);

  const currentMaxQuantity = currentAddonSub?.maxQuantity ?? 1;
  const tierCatalog = addonKey ? ADDON_TIER_CATALOG[addonKey] : undefined;
  const isTieredAddon = !!tierCatalog;
  const currentTier: Tier = (currentAddonSub?.planFeature as Tier | undefined) ?? 'standard';

  // scio1 fallback chain (subscriptionPlanModal.js:768):
  //   params.currentPlanTier → add_on.currentPlanTier → add_on.defaultPlan → 'standard'
  // The `currentPlanTier` dialog-context key lets callers preselect a tier
  // (e.g., from the list row "Update Licenses" click).
  const paramsCurrentPlanTier = dialogContext.currentPlanTier as Tier | undefined;
  const defaultPlanFromAddon = undefined; // addonFromPlan?.defaultPlan isn't loaded yet at state-init time
  const initialTier: Tier =
    paramsCurrentPlanTier ??
    (currentAddonSub?.planFeature as Tier | undefined) ??
    defaultPlanFromAddon ??
    'standard';

  // OptiDev bounds: min=3, max=max(totalAccountScreens, 3).
  // "Enable for all screens" pegs quantity to max and locks the stepper.
  const isOptidev = addonKey === OPTIDEV_ADDON_KEY;
  const optidevMinQty = 3;
  const optidevMaxQty = Math.max(maxScreens ?? 0, optidevMinQty);
  // Which of the three OptiDev stories this open is telling — subscribe (trial
  // running or over), info (paid, nothing addable) or update.
  const optidevMode = resolveOptidevMode(currentAddonSub, maxScreens ?? 0);

  // State
  const [quantity, setQuantity] = useState(currentMaxQuantity);
  const [selectedTier, setSelectedTier] = useState<Tier>(initialTier);
  const [enableAllScreens, setEnableAllScreens] = useState(!!currentAddonSub?.enableAllScreens);

  // Sync state when sub-account data loads
  useEffect(() => {
    if (subAccountId && !loadingSubAccount) {
      setQuantity(currentMaxQuantity);
      setSelectedTier(initialTier);
      setEnableAllScreens(!!currentAddonSub?.enableAllScreens);
    }
  }, [
    subAccountId,
    loadingSubAccount,
    currentMaxQuantity,
    initialTier,
    currentAddonSub?.enableAllScreens,
  ]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [finishResult, setFinishResult] = useState<FinishResult | null>(null);

  // Holds the PAYMENT_FAILED quantity auto-revert timer so we can clear it
  // if the dialog closes before it fires (avoids setState on unmounted).
  const paymentFailedRevertTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => {
      if (paymentFailedRevertTimerRef.current) {
        clearTimeout(paymentFailedRevertTimerRef.current);
      }
    };
  }, []);

  // Reset state only when dialog opens (not on refetch-driven data changes)
  const [prevOpen, setPrevOpen] = useState(false);
  if (isOpen && !prevOpen) {
    // OptiDev floor: clamp a stale entry sitting below minQuantity up to it.
    const initialQty = isOptidev
      ? Math.max(currentMaxQuantity || optidevMinQty, optidevMinQty)
      : currentMaxQuantity || 1;
    setQuantity(initialQty);
    setSelectedTier(initialTier);
    setEnableAllScreens(!!currentAddonSub?.enableAllScreens);
    setFinishResult(null);
    setPrevOpen(true);
  } else if (!isOpen && prevOpen) {
    setPrevOpen(false);
  }

  // "Enable for all screens" toggle: ON pegs quantity to total account screens; OFF restores ≥ min.
  const handleToggleAllScreens = useCallback(
    (next: boolean) => {
      setEnableAllScreens(next);
      if (next) {
        setQuantity(optidevMaxQty);
      } else {
        setQuantity((prev) => Math.max(prev, optidevMinQty));
      }
    },
    [optidevMaxQty, optidevMinQty],
  );

  const confirm = useConfirm();

  // Fetch plan feature for addon pricing
  const { data: planFeaturesData } = useQuery(PLAN_FEATURES_QUERY, {
    variables: { query: { alias: currentPlanAlias } },
    skip: !isOpen || !currentPlanAlias,
    fetchPolicy: 'network-only',
  });

  // Fetch Stripe-converted prices for isAutoConvertCurrency addons (scio1
  // subscriptionPlanModal.js:280-287). Skipped for USD accounts (catalog
  // already has the right value) and when the dialog is closed.
  const { data: currencyOptionsData } = useQuery<{
    getPriceAutoConvertCurrencies?: Record<
      string,
      Record<string, Record<string, CurrencyOptionEntry>>
    >;
  }>(GET_PRICE_AUTO_CONVERT_CURRENCIES, {
    variables: { currency },
    skip: !isOpen || currency === 'usd',
  });
  const currencyOptions = currencyOptionsData?.getPriceAutoConvertCurrencies;

  // Extract full addon object from plan features (needed for mutation — backend expects full addon data)
  const addonFromPlan = useMemo(() => {
    const node = extractPlanFeaturesNode(planFeaturesData);
    if (!node || !addonKey) return null;

    const addOns = node.add_ons as Record<string, Record<string, unknown>> | undefined;
    if (!addOns?.[addonKey]) return null;

    return { ...addOns[addonKey] };
  }, [planFeaturesData, addonKey]);

  // Account-level single-license add-on (e.g. Branding). SCIO 1.0 parity
  // (subscriptionPlanModal.html:98): the stepper is capped to min 0 / max 1
  // (0 = cancel, 1 = active) and the price drops the per-screen unit.
  const isOnetime = addonFromPlan?.type === 'onetime';

  // Extract pricing info for display
  const addonPricing = useMemo(() => {
    if (!addonFromPlan) return null;
    const priceMap = addonFromPlan.price as
      | Record<string, Record<string, { price?: number; name?: string }>>
      | undefined;
    const currentPlan = extPayment?.plan ?? 'yearly';
    const intervalKey = currentPlan === 'monthly' ? 'monthly' : 'yearly';
    const intervalSuffix = intervalKey === 'monthly' ? 'mo' : 'yr';
    // SCIO 1.0 parity (subscriptionPlanModal.js:813-828 getTierPrice): for
    // isAutoConvertCurrency addons, prefer the Stripe-converted price from
    // currencyOptions[key][priceKey][currency].unit_amount (cents). Falls back
    // to USD catalog entry when Stripe data isn't available, and finally to
    // user's currency lookup for non-auto-convert addons.
    const isAutoConvert = !!addonFromPlan.isAutoConvertCurrency;
    const addonKeyLocal = (addonFromPlan.key as string | undefined) ?? addonKey ?? '';
    const lookupCurrency = isAutoConvert ? 'usd' : currency;
    const convertedFor = (priceKey: string): number | undefined => {
      if (!isAutoConvert || currency === 'usd' || !addonKeyLocal) return undefined;
      const cents = currencyOptions?.[addonKeyLocal]?.[priceKey]?.[currency]?.unit_amount;
      return typeof cents === 'number' ? cents / 100 : undefined;
    };

    // Per-tier pricing for tiered add-ons; otherwise only the default single price.
    const tierPrices: Partial<Record<Tier, { price: number; intervalSuffix: string }>> = {};
    if (tierCatalog) {
      for (const { tier, priceKeyMonthly, priceKeyYearly } of tierCatalog) {
        const key = intervalKey === 'monthly' ? priceKeyMonthly : priceKeyYearly;
        const fallback = priceMap?.[lookupCurrency]?.[key]?.price ?? 0;
        tierPrices[tier] = { price: convertedFor(key) ?? fallback, intervalSuffix };
      }
    }

    // Default (non-tiered) lookup falls back to the current interval key;
    // for tiered add-ons, the selected-tier price is surfaced by the consumer.
    // For isAutoConvertCurrency addons (e.g. OptiDev), read both the flat base
    // and per-screen overage from the Stripe tier data via readAddonAmounts —
    // base can differ from perScreen*minQty (e.g. GBP £25 base but £8.35/screen).
    const converted =
      isAutoConvert && currency !== 'usd'
        ? readAddonAmounts(currencyOptions?.[addonKeyLocal]?.[intervalKey]?.[currency])
        : {};
    const defaultEntry = priceMap?.[lookupCurrency]?.[intervalKey];
    const defaultPrice = converted.perScreen ?? defaultEntry?.price ?? 0;
    return {
      price: defaultPrice,
      basePrice: converted.baseFlat, // flat base for tiered (OptiDev); undefined otherwise
      intervalName: defaultEntry?.name ?? intervalKey,
      intervalKey,
      intervalSuffix,
      tierPrices,
    };
  }, [addonFromPlan, addonKey, currency, currencyOptions, extPayment?.plan, tierCatalog]);

  // Selected-tier price drives the inline priceText and receipt fallback.
  const selectedTierPrice = isTieredAddon
    ? (addonPricing?.tierPrices?.[selectedTier]?.price ?? 0)
    : (addonPricing?.price ?? 0);

  // For tiered addons, match scio1's subscriptionPlanModal footer line which
  // includes the selected tier label and uses "device" as the unit.
  const selectedTierLabel = selectedTier === 'pro' ? t('Pro') : t('Standard');

  // OptiDev composes its own price lines in OptiDevAddonBody — this is for the
  // other add-ons only.
  const priceText = addonPricing
    ? isTieredAddon && addonName
      ? t('{{name}} Add-on — {{tier}}: {{price}}/{{interval}}/screen', {
          name: addonName,
          tier: selectedTierLabel,
          price: `${currencySymbol}${selectedTierPrice.toFixed(2)}`,
          interval: addonPricing.intervalName,
        })
      : isOnetime
        ? `${addonName} ${t('Add-on')}: ${currencySymbol}${selectedTierPrice.toFixed(2)}/${addonPricing.intervalName}`
        : `${addonName} ${t('Add-on')}: ${currencySymbol}${selectedTierPrice.toFixed(2)}/${addonPricing.intervalName}/${t('screen')}`
    : '';

  // Plan info for the receipt — name, billing cycle label, price per screen, screen count.
  // Mirrors scio1's clean receipt rendering instead of Stripe's auto-generated line descriptions.
  const currentPlanInfo = useMemo(() => {
    const node = extractPlanFeaturesNode(planFeaturesData);
    const planName = (node?.name as string) ?? getPlanDisplayName(currentPlanAlias);
    const intervalKey = extPayment?.plan === 'monthly' ? 'monthly' : 'yearly';
    const billingCycleLabel = BILLING_INTERVAL_LABELS[intervalKey] ?? intervalKey;
    const plansMap = node?.plans as Record<string, Record<string, { price?: number }>> | undefined;
    const pricePerScreen = plansMap?.[currency]?.[intervalKey]?.price ?? 0;
    return { planName, billingCycleLabel, pricePerScreen };
  }, [planFeaturesData, currentPlanAlias, extPayment?.plan, currency]);

  // Current screen count for the plan line item (separate from addon quantity)
  const planScreens = maxScreens ?? 0;

  // Mutations
  // The add-on card's `checked` / quantity state is derived from
  // `useBillingData → payment.addons`, which reads from `GetSubAccountInfo`
  // (sub-account context) or `GetTeamBillingInfo` (team context). Without
  // refetching those, the dialog closes successfully but the parent card
  // stays on the pre-update license count.
  // `GetSubAccountList` keeps the sub-account list table's Add-on column
  // accurate when the user clicks Back.
  const addonRefetchQueries = [
    'GetSubscriptionInfoStripe',
    'GetCompanyInfo',
    'GetUpcomingSubscription',
    // SP47 — refresh the per-addon "Licenses Used / Total" on the subscription-plan page
    'GetDevicesAddOn',
    'GetSubAccountInfo',
    'GetSubAccountList',
    'GetTeamBillingInfo',
  ];
  const [updateAddon] = useMutation(UPDATE_SUBSCRIPTION_ADDON, {
    refetchQueries: addonRefetchQueries,
  });
  const [editAddon] = useMutation(EDIT_SUBSCRIPTION_ADDON, {
    refetchQueries: addonRefetchQueries,
  });

  // Lazy query to fetch receipt data after successful update (matching scio1 initFinishPayment)
  const [fetchUpcoming] = useLazyQuery(GET_UPCOMING_SUBSCRIPTION, { fetchPolicy: 'network-only' });

  // Active paired-screen count for this addon. Two consumers:
  //   1. Pro → Standard downgrade warning (tiered/Pro path) — see `activeProScreens` below
  //   2. #1147 — clamp ScreenCountSelector min so user can't reduce maxQuantity
  //      below current usage; backend rejects with a generic "invalid quantity"
  //      otherwise (billing.service.ts:1128).
  // Was previously gated on isTieredAddon + currentTier === 'pro' (downgrade-only);
  // now runs for every open dialog so the min-clamp works for AeriCast/VideoWall too.
  const { data: devicesAddonData } = useQuery(GET_DEVICES_ADDON, {
    variables: {
      key: addonKey ?? '',
      offset: 0,
      limit: 1,
      subAccountId,
    },
    fetchPolicy: 'network-only',
    skip: !isOpen || !addonKey,
  });
  const currentUsageCount =
    (
      (devicesAddonData as Record<string, unknown> | undefined)?.getDevicesAddOn as
        | { totalUsed?: number }
        | undefined
    )?.totalUsed ?? 0;
  // Pro-tier-only view of the same number — preserves the previous semantics so
  // the downgrade warning only fires when user is actually on Pro tier.
  const activeProScreens = currentTier === 'pro' ? currentUsageCount : 0;
  const willDowngrade =
    isTieredAddon && currentTier === 'pro' && selectedTier === 'standard' && activeProScreens > 0;

  // existingCard comes from useBillingData

  // email comes from useBillingData as defaultEmail (aliased to `email`)

  const handleUpdate = useCallback(async () => {
    if (!addonKey) return;
    const tierChanged = isTieredAddon && selectedTier !== currentTier;
    const quantityChanged = quantity !== currentMaxQuantity;
    // Toggling "Enable for all screens" alone is a valid change even when the resulting
    // quantity matches current — the flag drives future auto-sync, so we must persist it.
    const enableAllScreensChanged =
      isOptidev && enableAllScreens !== !!currentAddonSub?.enableAllScreens;

    // Allow update when converting from trial to paid (even if quantity matches)
    if (!quantityChanged && !tierChanged && !enableAllScreensChanged && !currentAddonSub?.isTrial) {
      toast(t('No changes detected. Your subscription remains the same.'));
      return;
    }

    // #1147 — pre-check: backend rejects maxQuantity < activeDeviceCount with
    // a generic "invalid quantity" message (billing.service.ts:1128). Surface
    // a clear localized error and skip the round trip. Skip for "onetime"
    // addons (backend allows reducing them) and OptiDev (already enforces
    // optidevMinQty via the selector).
    if (
      !isOptidev &&
      addonFromPlan?.type !== 'onetime' &&
      quantityChanged &&
      quantity < currentUsageCount
    ) {
      toast.error(
        t(
          'You currently have {{n}} screen(s) using {{name}}. Minimum allowed is {{n}}. Remove screens from this Add-on first.',
          { n: currentUsageCount, name: addonName ?? addonKey },
        ),
      );
      return;
    }

    // Destructive downgrade guard — Pro → Standard with active Pro screens
    if (willDowngrade) {
      const confirmed = await confirm({
        title: t('Downgrade to Standard?'),
        description: t(
          '{{n}} screens currently use Pro stations. After downgrade they will stop playing music. Continue?',
          { n: activeProScreens },
        ),
        confirm: { label: t('Downgrade'), variant: 'destructive' },
        cancel: { label: t('Cancel') },
      });
      if (!confirmed) return;
    }

    // If reducing to 0, close immediately after success (matching scio1 line 1484-1487)
    const isReduceToZero = quantity === 0;

    setIsSubmitting(true);
    try {
      let result;
      let responseKey: 'updateSubscriptionAddOn' | 'editSubscriptionAddOn';

      if (tierChanged && !quantityChanged) {
        // Tier-only change — route through editSubscriptionAddOn (scio1 parity:
        // changeSubscription.js:3443-3483 uses this path for pure tier swaps).
        // `number: 0` is required — backend's AddOnInput treats it as the tier-only
        // flag (`isTierChangeOnly = selectedPlanTier && number === 0`). Omitting it
        // leaves `number` undefined, falling through to arithmetic that yields NaN.
        result = await editAddon({
          variables: {
            addOn: {
              key: addonKey.toUpperCase(),
              number: 0,
              selectedPlanTier: selectedTier,
            },
          },
        });
        responseKey = 'editSubscriptionAddOn';
      } else {
        // Quantity change (with or without tier) — updateSubscriptionAddOn handles both.
        result = await updateAddon({
          variables: {
            addOn: {
              key: addonKey.toUpperCase(),
              maxQuantity: quantity,
              type: (addonFromPlan?.type as string) ?? undefined,
              ...(isTieredAddon ? { selectedPlanTier: selectedTier } : {}),
              ...(isOptidev ? { enableAllScreens } : {}),
            },
            subAccountId,
          },
        });
        responseKey = 'updateSubscriptionAddOn';
      }
      const response = extractMutationResponse<Record<string, unknown>>(result.data, responseKey);
      if (response?.status) {
        if (isReduceToZero) {
          // Matching scio1: close immediately when reducing to 0
          closeDialog();
          return;
        }
        // Fetch upcoming subscription for receipt data (matching scio1 initFinishPayment)
        let totalPrice = 0;
        let credit = 0;
        let upcoming: Record<string, unknown> | undefined;
        let taxs: Array<{ name?: string; tax?: number }> | undefined;
        try {
          const stripePlanId = addonPricing
            ? ((
                addonFromPlan?.price as Record<string, Record<string, { stripe_plan_id?: string }>>
              )?.[currency]?.[extPayment?.plan === 'monthly' ? 'monthly' : 'yearly']
                ?.stripe_plan_id ?? '')
            : '';
          const { data: upcomingData } = await fetchUpcoming({
            variables: {
              isTrial: false,
              stripe_plan_id: stripePlanId,
              subAccountId,
            },
          });
          const raw = (upcomingData as Record<string, unknown>)?.getUpcomingSubscription as
            | Record<string, unknown>
            | undefined;
          // Backend returns total/credit already in dollars (invoice.amount_due/100, balance/100)
          totalPrice = (raw?.total as number) ?? 0;
          credit = (raw?.credit as number) ?? 0;
          upcoming = raw?.upcoming as Record<string, unknown> | undefined;
          // Tax line items for the Thank-You/Receipt card. Prefer NESTED
          // `upcoming.taxs` (next-period total_tax_amounts via
          // retrieveUpcoming({ automatic_tax: enabled })) — that's the value
          // that pairs with the Subtotal / Est Next Bill Total shown here.
          // Top-level `raw.taxs` is the proration-delta tax of the just-issued
          // invoice (a small adjustment), which doesn't reconcile with the
          // displayed subtotal. Fall back to top-level only when Stripe stripped
          // the nested field (legacy serialization edge case). See `a65fcf7d0`.
          const upcomingTaxs = (upcoming as { taxs?: unknown } | undefined)?.taxs;
          if (Array.isArray(upcomingTaxs) && upcomingTaxs.length > 0) {
            taxs = upcomingTaxs as Array<{ name?: string; tax?: number }>;
          } else if (Array.isArray(raw?.taxs)) {
            taxs = raw.taxs as Array<{ name?: string; tax?: number }>;
          }
        } catch (err) {
          reportPaymentError('payment-addon', err);
          // Receipt data unavailable, show basic receipt
        }

        // Snapshot all currently-active addons for the receipt at submit time —
        // see buildReceiptAddons() helper at the top of this file.
        const receiptAddons = buildReceiptAddons({
          paymentAddons,
          planFeaturesData,
          updatedAddonKey: addonKey,
          updatedQuantity: quantity,
          updatedPlanFeature: isTieredAddon ? selectedTier : undefined,
          currency,
          intervalKey: extPayment?.plan === 'monthly' ? 'monthly' : 'yearly',
          fallbackAddonName: addonName,
          fallbackPricePerUnit: selectedTierPrice,
          currencyOptions,
        });

        // Show finish/receipt screen — snapshot plan + addons state at charge time
        // so the receipt doesn't drift after refetch picks up the new quantities.
        setFinishResult({
          invoiceNumber: response.invoiceNumber as string | undefined,
          invoicePdf: response.invoicePdf as string | undefined,
          totalPrice,
          credit,
          upcoming,
          taxs,
          planName: currentPlanInfo.planName,
          planBillingCycleLabel: currentPlanInfo.billingCycleLabel,
          planPricePerScreen: currentPlanInfo.pricePerScreen,
          planScreens,
          addons: receiptAddons,
        });
      } else {
        const responseCode = response?.code;
        const message = (response?.message as string) ?? '';
        // #1147 — backend's generic "invalid quantity" (PAYMENT_STATUS_CODE.LIMIT_QUANTITY)
        // → replace with a clear localized message. Reaches here only on race
        // conditions (e.g. another tab added a device after we computed the min)
        // because the pre-check + min clamp normally block this.
        const isLimitQuantity =
          responseCode === 'limit_quantity' ||
          responseCode === 'LIMIT_QUANTITY' ||
          /invalid quantity/i.test(message);
        if (isLimitQuantity) {
          toast.error(
            t(
              'You currently have {{n}} screen(s) using {{name}}. Minimum allowed is {{n}}. Remove screens from this Add-on first.',
              { n: currentUsageCount, name: addonName ?? addonKey },
            ),
          );
        } else {
          reportPaymentError('payment-addon', response);
          toast.error(message || t('Payment failed. Please try again.'));
        }

        // scio1 parity (subscriptionPlanModal.js:1533-1537): on payment failure,
        // auto-revert the quantity after 2s so the form returns to the last good state.
        const isPaymentFailed =
          responseCode === 'PAYMENT_FAILED' ||
          responseCode === 'payment_failed' ||
          (typeof message === 'string' && /payment[_ ]failed/i.test(message));
        if (isPaymentFailed) {
          const prev = currentMaxQuantity;
          // Clear any prior timer, then schedule revert. The ref is cleared
          // by the unmount-cleanup effect so a dialog close within 2s won't
          // leave a setState call pending.
          if (paymentFailedRevertTimerRef.current) {
            clearTimeout(paymentFailedRevertTimerRef.current);
          }
          paymentFailedRevertTimerRef.current = setTimeout(() => {
            setQuantity(prev);
            paymentFailedRevertTimerRef.current = null;
          }, 2000);
        }
      }
    } catch (err) {
      reportPaymentError('payment-addon', err);
      const message = err instanceof Error ? err.message : t('Payment failed. Please try again.');
      toast.error(message);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    addonKey,
    quantity,
    currentMaxQuantity,
    currentAddonSub?.isTrial,
    subAccountId,
    updateAddon,
    editAddon,
    closeDialog,
    t,
    addonFromPlan,
    fetchUpcoming,
    currency,
    currencyOptions,
    extPayment?.plan,
    addonPricing,
    addonName,
    currentPlanInfo,
    planScreens,
    paymentAddons,
    planFeaturesData,
    isTieredAddon,
    selectedTier,
    currentTier,
    willDowngrade,
    activeProScreens,
    confirm,
    selectedTierPrice,
    isOptidev,
    enableAllScreens,
    currentAddonSub?.enableAllScreens,
    currentUsageCount,
  ]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open && isSubmitting) return;
      if (!open) {
        closeDialog();
        // Reset finish state after close animation
        setTimeout(() => setFinishResult(null), 200);
      }
    },
    [closeDialog, isSubmitting],
  );

  const isFinish = !!finishResult;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        className={cn(
          'max-h-[85vh] flex flex-col overflow-hidden',
          isTieredAddon ? 'sm:max-w-3xl' : 'sm:max-w-md',
        )}
        data-testid="addon-update-dialog"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => {
          if (isSubmitting) e.preventDefault();
        }}
        onOpenAutoFocus={(e) => {
          // OptiDev opens at qty=min=3 with the decrement button disabled,
          // so Radix's first-focusable auto-focus skipped to the Input and
          // the browser auto-selected its text. Suppress auto-focus only
          // for OptiDev — other add-ons keep the existing decrement-button
          // focus that supports immediate keyboard adjust.
          if (isOptidev) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>
            {isOptidev ? (
              optidevMode === 'subscribe' ? (
                t('Subscribe to {{name}} Add-on', { name: addonName })
              ) : (
                // Interpolated, not concatenated — word order varies by language.
                t('{{name}} Add-on', { name: addonName })
              )
            ) : (
              <>
                {isUpdate ? t('Update') : t('Add')} {addonName} {t('Add-on')}
              </>
            )}
          </DialogTitle>
        </DialogHeader>

        <ProcessingOverlay visible={isSubmitting} />

        {subAccountId && loadingSubAccount ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : isFinish ? (
          /* === Finish/Receipt screen — matching scio1 Image #9 === */
          <>
            <div className="flex-1 overflow-y-auto px-6 py-6">
              {/* Thank you header */}
              <div className="text-center mb-4">
                <h2 className="text-2xl font-semibold">{t('Thank you')}!</h2>
                {finishResult.invoiceNumber && (
                  <p className="text-sm text-muted-foreground mt-1">
                    {t('Invoice')} {finishResult.invoiceNumber}
                  </p>
                )}
              </div>

              {/* Receipt card — matching scio1's finish screen for addon mode */}
              <div className="rounded-lg border p-6 space-y-4">
                <h3 className="text-xl font-semibold text-center">{t('Receipt')}</h3>

                {email && (
                  <div className="text-center text-sm">
                    <p className="text-muted-foreground">
                      {t('Copy of receipt has been emailed to:')}
                    </p>
                    <a href={`mailto:${email}`} className="text-primary">
                      {email}
                    </a>
                  </div>
                )}

                <Separator />

                {/* Plan line item — clean local rendering matching scio1's
                    subscriptionPlanModal.html:823-872 (not Stripe lines.data) */}
                <div className="flex justify-between text-sm">
                  <div>
                    <p className="font-semibold">
                      {finishResult.planName} - {finishResult.planBillingCycleLabel}
                    </p>
                    <p className="text-muted-foreground">
                      {finishResult.planScreens}{' '}
                      {finishResult.planScreens === 1 ? t('screen') : t('screens')}
                    </p>
                  </div>
                  <span className="font-medium">
                    {formatCurrencyAmount(
                      (finishResult.planPricePerScreen ?? 0) * (finishResult.planScreens ?? 0),
                      currency,
                    )}
                  </span>
                </div>

                {/* Active addon line items — matches scio1 subscriptionPlanModal.html:850-872
                    which iterates getAddOnsChecked() so EVERY active addon appears, not just
                    the one being updated. */}
                {finishResult.addons?.map((addon) => {
                  const tierLabel =
                    addon.planFeature === 'pro'
                      ? t('Pro')
                      : addon.planFeature === 'standard'
                        ? t('Standard')
                        : null;
                  const displayName = tierLabel
                    ? `${addon.name} ${t('Add-on')} — ${tierLabel}`
                    : `${addon.name} ${t('Add-on')}`;
                  return (
                    <div key={addon.key} className="flex justify-between text-sm">
                      <div>
                        <p className="font-semibold">{displayName}</p>
                        <p className="text-muted-foreground">
                          {addon.quantity} {addon.quantity === 1 ? t('screen') : t('screens')}
                        </p>
                      </div>
                      <span className="font-medium">
                        {formatCurrencyAmount(addon.pricePerUnit * addon.quantity, currency)}
                      </span>
                    </div>
                  );
                })}

                <Separator />

                {/* Subtotal — sum of plan + ALL active addons (matches scio1 getSubtotalPrice) */}
                {(() => {
                  const planTotal =
                    (finishResult.planPricePerScreen ?? 0) * (finishResult.planScreens ?? 0);
                  const addonTotal = (finishResult.addons ?? []).reduce(
                    (sum, a) => sum + a.pricePerUnit * a.quantity,
                    0,
                  );
                  const subtotal = planTotal + addonTotal;
                  const creditVal = finishResult.credit ?? 0;
                  // Backend returns amount_due in cents on the upcoming invoice — divide here
                  const estNextBill = finishResult.upcoming
                    ? Math.abs((finishResult.upcoming.amount_due as number) ?? 0) / 100
                    : 0;
                  // Backend returns totalPrice already in dollars (invoice.amount_due / 100)
                  const totalPaidToday = finishResult.totalPrice ?? 0;
                  return (
                    <>
                      <div className="flex justify-between text-sm">
                        <span className="font-semibold">{t('Subtotal')}</span>
                        <span className="font-medium">
                          {formatCurrencyAmount(subtotal, currency)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">
                          {t('Credit for previous purchase')}
                        </span>
                        <span className="text-primary">
                          {formatCurrencyAmount(creditVal, currency)}
                        </span>
                      </div>

                      {/* Tax line items (e.g. "VAT - Vietnam(10%)") — matches scio1
                          subscriptionPlanModal.html:891 placement between Credit and Est Next Bill */}
                      {finishResult.taxs && finishResult.taxs.length > 0 && (
                        <>
                          <Separator />
                          {finishResult.taxs.map((tax, i) => (
                            <div key={i} className="flex justify-between text-sm">
                              <span className="font-semibold">{tax.name}</span>
                              <span>{formatCurrencyAmount((tax.tax ?? 0) / 100, currency)}</span>
                            </div>
                          ))}
                        </>
                      )}

                      <Separator />

                      <div className="flex justify-between text-sm">
                        <span className="font-semibold">{t('Est Next Bill Total')}</span>
                        <span className="font-medium">
                          {formatCurrencyAmount(estNextBill, currency)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="font-semibold">{t('Total paid today for changes')}</span>
                        <span className="font-medium">
                          {formatCurrencyAmount(totalPaidToday, currency)}
                        </span>
                      </div>
                    </>
                  );
                })()}

                {/* Payment method */}
                {existingCard && (
                  <p className="text-center text-sm text-muted-foreground">
                    {t('Payment method')} <span className="uppercase">{existingCard.brand}</span>
                    {existingCard.last4 ? ` - ${existingCard.last4}` : ''}
                  </p>
                )}

                {/* Download Invoice link */}
                {finishResult.invoicePdf && (
                  <div className="text-center">
                    <a
                      href={finishResult.invoicePdf}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-primary underline cursor-pointer"
                    >
                      {t('Download Invoice/Receipt')}
                    </a>
                  </div>
                )}

                <Separator />

                {/* Continue button */}
                <div className="text-center">
                  <Button onClick={() => handleOpenChange(false)} className="cursor-pointer px-8">
                    {t('Continue')}
                  </Button>
                </div>
              </div>
            </div>
          </>
        ) : isOptidev ? (
          /* === OptiDev edit screen — its own body so the shared tree below
                keeps rendering unchanged for every other add-on. === */
          <OptiDevAddonBody
            mode={optidevMode}
            quantity={quantity}
            onQuantityChange={setQuantity}
            maxScreens={maxScreens ?? 0}
            usedLicenses={currentUsageCount}
            enableAllScreens={enableAllScreens}
            onToggleAllScreens={handleToggleAllScreens}
            basePrice={addonPricing?.basePrice}
            perScreenPrice={addonPricing?.price ?? 0}
            intervalKey={addonPricing?.intervalKey === 'monthly' ? 'monthly' : 'yearly'}
            currency={currency}
            isOnTrial={isOptidevOnTrial(currentAddonSub)}
            trialEndDate={currentAddonSub?.trialEndDate}
            helpLink={addonFromPlan?.helpLink as string | undefined}
            isSubmitting={isSubmitting}
            onCancel={() => handleOpenChange(false)}
            onSubmit={handleUpdate}
          />
        ) : (
          /* === Edit screen — tier selection (when tiered) + number selector + price === */
          <>
            <div className="flex-1 overflow-y-auto px-6 pt-6 pb-4 space-y-6">
              {!isTieredAddon && (
                <h2 className="text-xl font-semibold text-center">
                  {t('Number of')} {addonName} {t('Add-on')}
                </h2>
              )}

              {isTieredAddon && addonKey && addonPricing && (
                <AddonTierRadioCards
                  addonKey={addonKey}
                  selectedTier={selectedTier}
                  onTierChange={setSelectedTier}
                  pricing={addonPricing.tierPrices}
                  currencySymbol={currencySymbol}
                  currentTier={
                    // Match scio1: show "Current Plan" badge on the card matching
                    // the user's active (non-trial) tier. Tiered addons always
                    // have an effective tier even when the backend hasn't
                    // persisted `planFeature` — default to 'standard' per scio1
                    // `currentPlanTier || 'standard'` fallback chain.
                    currentAddonSub?.isActive && !currentAddonSub?.isTrial ? currentTier : null
                  }
                  disabled={isSubmitting}
                />
              )}

              <div className="flex flex-col items-center">
                <ScreenCountSelector
                  value={quantity}
                  onChange={setQuantity}
                  min={isOnetime ? 0 : currentUsageCount}
                  max={isOnetime ? 1 : undefined}
                  hideCircle={isTieredAddon}
                />

                {priceText && (
                  <p className="text-sm text-muted-foreground text-center mt-4 whitespace-pre-line leading-relaxed">
                    {priceText}
                  </p>
                )}
              </div>

              <TierDowngradeWarning activeProScreens={activeProScreens} visible={willDowngrade} />
            </div>

            {/* Footer — Cancel + (optional Learn more) + Update.
                DialogFooter applies -mx-6 -mb-6 so it sits flush with the dialog
                edges + bottom; the border-t separates it visually from the body. */}
            <DialogFooter className="border-t sm:justify-between">
              <Button
                variant="outline"
                onClick={() => handleOpenChange(false)}
                className="cursor-pointer"
              >
                {t('Cancel')}
              </Button>
              <div className="flex items-center gap-3">
                {/* Learn more — scio1 parity (subscriptionPlanModal.html:1071-1074):
                    closes the dialog on click and opens helpLink in a new tab. */}
                {(addonFromPlan?.helpLink as string | undefined) && (
                  <Button
                    variant="link"
                    asChild
                    className="cursor-pointer text-primary hover:brightness-75 transition-[filter]"
                  >
                    <a
                      href={addonFromPlan?.helpLink as string}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => handleOpenChange(false)}
                      className="inline-flex items-center gap-1"
                    >
                      {t('Learn more')}
                      <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                    </a>
                  </Button>
                )}
                <Button
                  onClick={handleUpdate}
                  disabled={isSubmitting}
                  data-testid="addon-update-submit-btn"
                  className={cn(isSubmitting ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer')}
                >
                  {isUpdate ? t('Update') : t('Add')}
                </Button>
              </div>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
